import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  emailRegExpValidator,
  Form,
  InputField,
  requiredValidator
} from '@monorepo/forms';
import { useI18n } from '@monorepo/i18n';
import { formatErrorMessage, TGRPCPromiseReject } from '@monorepo/grpc';
import { useNotifications } from '@monorepo/notification';
import { useSiteSettings } from '@monorepo/site-settings';
import SubmitButton from '../../../../common/SubmitButton';
import useCreateTemporaryUser from '../../../../hooks/useCreateTemporaryUser';
import { Roles } from '../../../../constants';

type FormValues = {
  firstName: string;
  lastName: string;
  businessName: string;
  email: string;
  password: string;
  isLimited: boolean;
};

const TempoparyUserRegisterForm = () => {
  const navigate = useNavigate();
  const { generalRoutes } = useSiteSettings();
  const { home = '/' } = generalRoutes || {};
  const { t } = useI18n();
  const { mutate } = useCreateTemporaryUser();
  const { errorNotification, successNotification } = useNotifications();

  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const phone = queryParams.get('phone');
  const role = queryParams.get('role');

  const onSuccess = useCallback(() => {
    successNotification(t('createUser.form.success'));
    navigate(home);
  }, [t, successNotification]);

  const onError = useCallback(
    (data: TGRPCPromiseReject) => {
      const message = formatErrorMessage(data, t);
      return errorNotification(message);
    },
    [errorNotification, t]
  );

  const defaultValues: FormValues = {
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    password: '',
    isLimited: true
  };

  const handleSubmit = useCallback((data: FormValues) => {
    mutate(
      {
        rawPassword: data.password,
        // @ts-ignore
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: phone!,
          businessName: data.businessName,
          email: data.email,
          role: role || Roles.SUPPLIER,
          isLimited: true
        }
      },
      { onSuccess, onError }
    );
  }, []);

  return (
    // @ts-ignore
    <Form<FormValues>
      onSubmit={handleSubmit}
      formSettings={{ defaultValues, mode: 'onChange' }}
    >
      <InputField
        id="firstName"
        name="firstName"
        label={t('register.form.label.name')}
        placeholder={t('register.form.placeholder.name')}
        rules={{ ...requiredValidator() }}
      />
      <InputField
        id="lastName"
        name="lastName"
        label={t('register.form.label.lastName')}
        placeholder={t('register.form.placeholder.lastName')}
        rules={{ ...requiredValidator() }}
      />
      <InputField
        id="businessName"
        name="businessName"
        label={t('register.form.label.companyName')}
        placeholder={t('register.form.placeholder.companyName')}
        rules={{ ...requiredValidator() }}
      />
      <InputField
        id="email"
        name="email"
        label={t('register.form.label.email')}
        placeholder={t('register.form.placeholder.email')}
        rules={{ ...requiredValidator(), ...emailRegExpValidator() }}
      />
      <InputField
        id="password"
        name="password"
        type="password"
        label={t('register.form.label.password')}
        placeholder={t('register.form.placeholder.password')}
        rules={{ ...requiredValidator() }}
      />
      <SubmitButton>{t('register.form.button.next')}</SubmitButton>
    </Form>
  );
};

export default TempoparyUserRegisterForm;
